import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LEAD_RATING_MAP,
  LEAD_STATUS_MAP,
  MAX_META_MESSAGE_CHARS,
  META_CHANNEL_KIND_DEFS,
  META_CHANNEL_KIND_MAP,
  META_MESSAGE_STATUS_MAP,
  MESSAGE_CHANNEL_MAP,
  WHATSAPP_REPLY_WINDOW_HOURS,
  whatsappNumber,
  withinReplyWindow,
  type MessageChannel,
  type LeadRating,
  type LeadStatus,
  type MetaChannelKind,
  type MetaChannelView,
  type MetaConversationDetail,
  type MetaConversationView,
  type MetaMessageView,
  type MetaStatus,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { date, dateTime, money, relative, time } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { ChannelLogo } from '../../components/ChannelLogo';
import { Badge, EmptyState, ErrorState, Modal, PageHead } from '../../components/ui';
import { Avatar, useTeamDirectory } from '../../components/People';
import { RecordPicker, type RecordLink } from '../../components/RecordPicker';
import { useToast } from '../../components/Toast';

/**
 * صندوق قنوات ميتا — بواجهة واتساب.
 *
 * رسائل العملاء من واتساب وماسنجر وإنستجرام في مكانٍ واحد يشبه واتساب: مرشّح
 * بشعارات القنوات، وقائمة محادثات، ومجرى فقاعات، وبدءُ محادثةٍ جديدة برقمٍ أو
 * بعميل. والربط وفكّه في الإعدادات وحدها — هذه الشاشة للمراسلة لا للضبط.
 *
 * ## والشاشة لا تُظهر ما لا يعمل
 *
 * الاستقبال يعمل بمجرّد ضبط الخادم؛ والإرسال يحتاج رمز وصولٍ لكل قناة. فحقل
 * الكتابة يُعطَّل حين لا رمز، ومكانه سببٌ يُقرأ — لا زرُّ إرسالٍ يبتلع الرسالة.
 */

/** الصندوق يُسأل كل عشر ثوانٍ: الوارد يصل بخطّاف لا نراه، فنكتشفه بالسحب. */
const POLL_MS = 10_000;

type KindFilter = MetaChannelKind | 'ALL';

export function Channels() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [kindFilter, setKindFilter] = useState<KindFilter>('ALL');
  const [composing, setComposing] = useState(false);
  /* لوحة التفاصيل مفتوحةٌ افتراضًا مع المحادثة — تُطوى بزرّ المعلومات في الرأس. */
  const [showPanel, setShowPanel] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setTerm(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const status = useQuery({
    queryKey: ['meta', 'status'],
    queryFn: () => api<MetaStatus>('/meta/status'),
  });

  const channels = useQuery({
    queryKey: ['meta', 'channels'],
    queryFn: () => api<MetaChannelView[]>('/meta/channels'),
  });

  const conversations = useQuery({
    queryKey: ['meta', 'conversations', term, unreadOnly],
    queryFn: () =>
      api<MetaConversationView[]>(
        `/meta/conversations${qs({ q: term || undefined, unreadOnly: unreadOnly || undefined })}`,
      ),
    refetchInterval: POLL_MS,
  });

  const channelList = channels.data ?? [];
  const rows = conversations.data ?? [];
  const hasChannels = channelList.length > 0;

  /* أنواع القنوات المربوطة فعلًا — لا تُعرض تبويبة لنوعٍ لا قناة له. */
  const presentKinds = useMemo(() => {
    const set = new Set(channelList.map((c) => c.kind));
    return META_CHANNEL_KIND_DEFS.filter((d) => set.has(d.key));
  }, [channelList]);

  const filtered = kindFilter === 'ALL' ? rows : rows.filter((c) => c.channelKind === kindFilter);
  const active = rows.find((c) => c.id === openId) ?? null;

  /* عدّاد غير المقروء لكل نوع — يظهر على التبويبة. */
  const unreadByKind = (kind: KindFilter) =>
    (kind === 'ALL' ? rows : rows.filter((c) => c.channelKind === kind)).reduce(
      (n, c) => n + c.unread,
      0,
    );

  const openConversation = (id: string) => {
    setOpenId(id);
    void api(`/meta/conversations/${id}/read`, { method: 'POST' }).then(() =>
      queryClient.invalidateQueries({ queryKey: ['meta', 'conversations'] }),
    );
  };

  const waChannels = channelList.filter((c) => c.kind === 'WHATSAPP' && c.isActive);

  /*
   * وصلات أيقونات التواصل في القوائم:
   *   ?conversation= يفتح محادثةً قائمة بمعرّفها،
   *   ?to=رقم (وقد يرافقه lead/account) يبدأ محادثةً هنا لا في wa.me —
   * فالحديث كله يبقى في النظام ويدخل سجلّ العميل.
   */
  const handledRef = useRef('');
  useEffect(() => {
    const cid = params.get('conversation');
    const to = params.get('to');
    const key = `${cid ?? ''}|${to ?? ''}`;
    if (!cid && !to) return;
    /* مفتاحُ آخر معالجةٍ لا قفلٌ دائم — نقرةٌ ثانية لرقمٍ آخر تُعالَج. */
    if (handledRef.current === key) return;

    if (cid) {
      handledRef.current = key;
      openConversation(cid);
      setParams(new URLSearchParams(), { replace: true });
      return;
    }

    if (!channels.data) return; /* انتظر القنوات قبل الحكم بغيابها */

    handledRef.current = key;
    const lead = params.get('lead');
    const account = params.get('account');
    setParams(new URLSearchParams(), { replace: true });

    const wa = channels.data.filter((c) => c.kind === 'WHATSAPP' && c.isActive);
    if (wa.length === 0) {
      toast('لا قناة واتساب مربوطة — اربطها من الإعدادات لتبدأ المحادثات من هنا.');
      return;
    }

    void api<MetaConversationView>('/meta/conversations', {
      method: 'POST',
      body: {
        channelId: wa[0]!.id,
        phone: to,
        leadId: lead ?? undefined,
        accountId: account ?? undefined,
      },
    })
      .then((conv) => {
        void queryClient.invalidateQueries({ queryKey: ['meta', 'conversations'] });
        setKindFilter('ALL');
        openConversation(conv.id);
      })
      .catch((e: unknown) => toast(e instanceof Error ? e.message : 'تعذّر بدء المحادثة.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, channels.data]);

  return (
    <div className="page">
      <PageHead
        title="قنوات ميتا"
        subtitle="واتساب وماسنجر وإنستجرام — رسائل العملاء داخل النظام"
      />

      {status.data && !status.data.canReceive && !status.data.elmujibReady && (
        <div className="alert alert-warning mb-4">
          <Icon name="alert" size={18} />
          <div>
            الاستقبال غير مضبوط بعد، فلا تصل رسائل.{' '}
            <Link to="/settings/meta">راجع إعدادات القنوات</Link> لمعرفة ما ينقص.
          </div>
        </div>
      )}

      {!hasChannels ? (
        <EmptyState
          title="لا قناة مربوطة بعد"
          text="اربط رقم واتساب أو صفحة فيسبوك من الإعدادات لتصل رسائل العملاء هنا."
          action={
            <Link to="/settings/meta" className="btn btn-primary">
              اربط قناة
            </Link>
          }
        />
      ) : (
        <div className={`wa-app${active && showPanel ? ' has-panel' : ''}`}>
          {/* ---------- الشريط الجانبيّ ---------- */}
          <aside className="wa-side">
            {/* مرشّح القنوات بشعاراتها. */}
            <div className="wa-tabs" role="tablist">
              <FilterTab
                active={kindFilter === 'ALL'}
                onClick={() => setKindFilter('ALL')}
                label="الكل"
                unread={unreadByKind('ALL')}
              />
              {presentKinds.map((d) => (
                <FilterTab
                  key={d.key}
                  active={kindFilter === d.key}
                  onClick={() => setKindFilter(d.key)}
                  logo={d.key}
                  label={d.label}
                  unread={unreadByKind(d.key)}
                />
              ))}
            </div>

            <div className="wa-search">
              <Icon name="search" size={15} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="بحث بالاسم أو الرقم"
                aria-label="بحث"
              />
              {search && (
                <button className="wa-search-x" onClick={() => setSearch('')} aria-label="مسح">
                  <Icon name="x" size={13} />
                </button>
              )}
            </div>

            <label className="wa-unread-toggle">
              <input
                type="checkbox"
                checked={unreadOnly}
                onChange={(e) => setUnreadOnly(e.target.checked)}
              />
              غير المقروءة فقط
            </label>

            {/* ما جدولته إدارة العملاء للإرسال من هذه القنوات — يظهر حيث يُرسَل. */}
            <OutgoingQueue />


            {conversations.isError && (
              <ErrorState error={conversations.error} onRetry={() => void conversations.refetch()} />
            )}

            <ul className="wa-list">
              {conversations.data && filtered.length === 0 && (
                <li className="wa-empty tiny mute">
                  {rows.length === 0
                    ? 'لا محادثات بعد. ستظهر هنا أوّل ما يراسلك عميل، أو ابدأ برسالة جديدة.'
                    : 'لا محادثات في هذا المرشّح.'}
                </li>
              )}

              {filtered.map((c) => (
                <li key={c.id}>
                  <button
                    className={`wa-row${c.id === openId ? ' is-active' : ''}`}
                    onClick={() => openConversation(c.id)}
                  >
                    <span className="wa-row-avatar">
                      <Avatar name={c.contactName ?? c.externalId} size={44} />
                      <span className="wa-row-badge">
                        <ChannelLogo kind={c.channelKind} size={15} />
                      </span>
                    </span>
                    <span className="wa-row-main">
                      <span className="wa-row-top">
                        <strong className="wa-row-title">
                          {c.contactName ?? c.contactPhone ?? c.externalId}
                        </strong>
                        {c.lastAt && <span className="wa-row-time">{time(c.lastAt)}</span>}
                      </span>
                      <span className="wa-row-bottom">
                        <span className="wa-row-last">{c.lastBody ?? 'لا رسائل'}</span>
                        {c.unread > 0 && <span className="wa-unread num">{c.unread}</span>}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            {/* زرٌّ عائمٌ لرسالةٍ جديدة — أسفل يمين القائمة كواتساب. */}
            {waChannels.length > 0 && (
              <button
                className="wa-fab"
                onClick={() => setComposing(true)}
                title="رسالة جديدة"
                aria-label="رسالة جديدة"
              >
                <Icon name="chat" size={22} />
              </button>
            )}
          </aside>

          {/* ---------- المحادثة ---------- */}
          <section className="wa-main">
            {active ? (
              <Thread
                key={active.id}
                conversation={active}
                channel={channelList.find((c) => c.id === active.channelId) ?? null}
                panelOpen={showPanel}
                onTogglePanel={() => setShowPanel((v) => !v)}
              />
            ) : (
              <div className="wa-blank">
                <div className="wa-blank-badge">
                  <Icon name="chat" size={34} />
                </div>
                <p className="mute">اختر محادثةً من القائمة، أو ابدأ رسالةً جديدة.</p>
              </div>
            )}
          </section>

          {/* ---------- لوحة تفاصيل جهة الاتصال ---------- */}
          {active && showPanel && (
            <ContactPanel
              key={active.id}
              conversation={active}
              onClose={() => setShowPanel(false)}
            />
          )}
        </div>
      )}

      {composing && (
        <NewMessageModal
          channels={waChannels}
          onClose={() => setComposing(false)}
          onStarted={(conv) => {
            setComposing(false);
            void queryClient.invalidateQueries({ queryKey: ['meta', 'conversations'] });
            setKindFilter('ALL');
            setOpenId(conv.id);
          }}
        />
      )}
    </div>
  );
}

/** تبويبة مرشّحٍ بشعارٍ أو نصّ، وعدّاد غير مقروء. */
function FilterTab({
  active,
  onClick,
  logo,
  label,
  unread,
}: {
  active: boolean;
  onClick: () => void;
  logo?: MetaChannelKind;
  label: string;
  unread: number;
}) {
  return (
    <button
      className={`wa-tab${active ? ' is-active' : ''}`}
      onClick={onClick}
      role="tab"
      aria-selected={active}
      title={label}
    >
      {logo ? <ChannelLogo kind={logo} size={18} /> : <Icon name="layers" size={16} />}
      <span className="wa-tab-label">{label}</span>
      {unread > 0 && <span className="wa-tab-badge num">{unread}</span>}
    </button>
  );
}

/* ==========================================================================
   بدء محادثة جديدة
   ========================================================================== */

/**
 * رسالة جديدة: رقمٌ يُكتب أو عميلٌ يُختار، وقناةٌ تُرسَل منها.
 *
 * الرقم الجديد يبدأ محادثةً بلا سجلّ، والعميل يبدؤها مربوطةً بملفّه — والخادم
 * يأخذ رقمه من ملفّه. وحين تكون قناة واتساب واحدة تُختار تلقائيًّا.
 */
function NewMessageModal({
  channels,
  onClose,
  onStarted,
}: {
  channels: MetaChannelView[];
  onClose: () => void;
  onStarted: (conv: MetaConversationView) => void;
}) {
  const toast = useToast();
  const [channelId, setChannelId] = useState(channels[0]?.id ?? '');
  const [mode, setMode] = useState<'phone' | 'record'>('phone');
  const [phone, setPhone] = useState('');
  const [picked, setPicked] = useState<{ link: RecordLink; name: string } | null>(null);

  const start = useMutation({
    mutationFn: () =>
      api<MetaConversationView>('/meta/conversations', {
        method: 'POST',
        body:
          mode === 'record' && picked
            ? {
                channelId,
                leadId: picked.link.leadId ?? undefined,
                accountId: picked.link.accountId ?? undefined,
              }
            : { channelId, phone: phone.trim() },
      }),
    onSuccess: (conv) => onStarted(conv),
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر بدء المحادثة.'),
  });

  const canSubmit =
    Boolean(channelId) &&
    (mode === 'record' ? Boolean(picked) : phone.replace(/\D/g, '').length >= 6);

  return (
    <Modal title="رسالة جديدة" onClose={onClose}>
      {channels.length > 1 && (
        <label className="field">
          <span className="label">القناة</span>
          <select
            className="select"
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
          >
            {channels.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="row mb-4" style={{ gap: 2 }}>
        <button
          className={`btn btn-sm${mode === 'phone' ? ' btn-primary' : ''}`}
          onClick={() => setMode('phone')}
        >
          <Icon name="phone" size={14} />
          رقم جديد
        </button>
        <button
          className={`btn btn-sm${mode === 'record' ? ' btn-primary' : ''}`}
          onClick={() => setMode('record')}
        >
          <Icon name="user" size={14} />
          اختر عميلًا
        </button>
      </div>

      {mode === 'phone' ? (
        <label className="field">
          <span className="label">رقم الواتساب</span>
          <input
            className="input num"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="201001234567"
            dir="ltr"
            inputMode="tel"
          />
          <span className="tiny mute">بمفتاح الدولة، بلا + أو صفرٍ بادئ.</span>
        </label>
      ) : picked ? (
        <div className="field">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span className="strong">
              <Icon name="user" size={14} /> {picked.name}
            </span>
            <button className="btn btn-sm" onClick={() => setPicked(null)}>
              تغيير
            </button>
          </div>
          <span className="tiny mute">يُؤخذ رقمه من ملفّه.</span>
        </div>
      ) : (
        <RecordPicker onPick={(link, name) => setPicked({ link, name })} />
      )}

      <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
        <button
          className="btn btn-primary"
          onClick={() => start.mutate()}
          disabled={!canSubmit || start.isPending}
        >
          {start.isPending ? 'جارٍ الفتح…' : 'ابدأ المحادثة'}
        </button>
        <button className="btn" onClick={onClose}>
          إلغاء
        </button>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   المحادثة
   ========================================================================== */

function Thread({
  conversation,
  channel,
  panelOpen,
  onTogglePanel,
}: {
  conversation: MetaConversationView;
  channel: MetaChannelView | null;
  panelOpen: boolean;
  onTogglePanel: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState('');
  const [linking, setLinking] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);

  const messages = useQuery({
    queryKey: ['meta', 'messages', conversation.id],
    queryFn: () => api<MetaMessageView[]>(`/meta/conversations/${conversation.id}/messages`),
    refetchInterval: POLL_MS,
  });

  const rows = messages.data ?? [];
  const lastId = rows.length ? rows[rows.length - 1]!.id : null;

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [lastId, conversation.id]);

  const send = useMutation({
    mutationFn: (body: string) =>
      api<MetaMessageView>(`/meta/conversations/${conversation.id}/messages`, {
        method: 'POST',
        body: { body },
      }),
    onSuccess: (m) => {
      setDraft('');
      void queryClient.invalidateQueries({ queryKey: ['meta'] });
      if (m.status === 'FAILED') toast(m.error ?? 'لم تصل الرسالة.');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الإرسال.'),
  });

  const link = useMutation({
    mutationFn: (body: { leadId?: string | null; accountId?: string | null }) =>
      api(`/meta/conversations/${conversation.id}/link`, { method: 'PATCH', body }),
    onSuccess: () => {
      setLinking(false);
      void queryClient.invalidateQueries({ queryKey: ['meta', 'conversations'] });
      /* لوحة التفاصيل تقرأ السجلّ المربوط، فتُحدَّث بعد الربط. */
      void queryClient.invalidateQueries({ queryKey: ['meta', 'detail', conversation.id] });
      toast('رُبطت المحادثة');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الربط.'),
  });

  const canSend = Boolean(channel?.canSend) && Boolean(channel?.isActive);
  const isWhatsApp = conversation.channelKind === 'WHATSAPP';
  /* المحادثة الجديدة (بلا رسائل) تُسمح ببدئها؛ بعدها تسري نافذة الردّ. */
  const fresh = rows.length === 0 && !conversation.lastInboundAt;
  const windowOpen = !isWhatsApp || fresh || withinReplyWindow(conversation.lastInboundAt);

  return (
    <>
      <header className="wa-head">
        <Avatar name={conversation.contactName ?? conversation.externalId} size={40} />
        <div className="wa-head-who">
          <h2 className="wa-head-title">
            {conversation.contactName ?? conversation.contactPhone ?? conversation.externalId}
          </h2>
          <p className="wa-head-sub">
            <ChannelLogo kind={conversation.channelKind} size={13} />
            {META_CHANNEL_KIND_MAP[conversation.channelKind].label} · {conversation.channelName}
            {conversation.contactPhone && ` · ${conversation.contactPhone}`}
          </p>
        </div>

        <div className="wa-head-actions">
          {conversation.leadId ? (
            <Link to={`/crm/leads/${conversation.leadId}`} className="btn btn-sm">
              <Icon name="funnel" size={13} /> {conversation.leadName}
            </Link>
          ) : conversation.accountId ? (
            <Link to={`/crm/accounts/${conversation.accountId}`} className="btn btn-sm">
              <Icon name="building" size={13} /> {conversation.accountName}
            </Link>
          ) : (
            <button className="btn btn-sm" onClick={() => setLinking(true)}>
              <Icon name="funnel" size={14} />
              اربطها بعميل
            </button>
          )}
          <button
            className={`btn btn-sm btn-icon${panelOpen ? ' is-active' : ''}`}
            onClick={onTogglePanel}
            title={panelOpen ? 'إخفاء التفاصيل' : 'تفاصيل جهة الاتصال'}
            aria-label="تفاصيل جهة الاتصال"
            aria-pressed={panelOpen}
          >
            <Icon name="info" size={16} />
          </button>
        </div>
      </header>

      <div className="wa-stream">
        {messages.isPending && <div className="skeleton" style={{ height: 120 }} />}
        {messages.isError && (
          <ErrorState error={messages.error} onRetry={() => void messages.refetch()} />
        )}

        {messages.data && rows.length === 0 && (
          <p className="wa-daychip">لا رسائل بعد — اكتب أوّل رسالة.</p>
        )}

        {rows.map((m) => (
          <article key={m.id} className={`wa-msg${m.direction === 'OUTBOUND' ? ' is-out' : ''}`}>
            <div className="wa-bubble">
              {m.body ? (
                <p className="wa-text">{m.body}</p>
              ) : (
                <p className="tiny mute">[{m.mediaType ?? 'مرفق'}]</p>
              )}

              <span className="wa-msg-meta">
                <span title={dateTime(m.sentAt)}>{relative(m.sentAt)}</span>
                {m.direction === 'OUTBOUND' && (
                  <Badge kind={META_MESSAGE_STATUS_MAP[m.status].kind}>
                    {META_MESSAGE_STATUS_MAP[m.status].label}
                  </Badge>
                )}
              </span>

              {m.error && <p className="tiny meta-error">{m.error}</p>}
            </div>
          </article>
        ))}

        <div ref={bottom} />
      </div>

      {canSend && windowOpen && (
        <div className="wa-composer">
          {/* الرسائل المحفوظة من الإعدادات — تُدرَج في المسودّة بنقرة. */}
          <SavedInsert
            channel={conversation.channelKind}
            onInsert={(body) => setDraft((d) => (d ? `${d}\n${body}` : body).slice(0, MAX_META_MESSAGE_CHARS))}
          />
          <textarea
            className="textarea wa-input"
            rows={1}
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, MAX_META_MESSAGE_CHARS))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (draft.trim()) send.mutate(draft.trim());
              }
            }}
            placeholder="اكتب رسالة…"
          />
          <button
            className="btn btn-primary btn-icon wa-send"
            onClick={() => draft.trim() && send.mutate(draft.trim())}
            disabled={send.isPending || !draft.trim()}
            title="إرسال"
            aria-label="إرسال"
          >
            <Icon name="send" size={17} />
          </button>
        </div>
      )}

      {!canSend && (
        <div className="meta-blocked">
          <Icon name="alert" size={15} />
          <span className="small">
            هذه القناة بلا رمز وصول، فلا يمكن الردّ منها.{' '}
            <Link to="/settings/meta">أضِف الرمز</Link> — الاستقبال يعمل بدونه.
          </span>
        </div>
      )}

      {canSend && !windowOpen && (
        <div className="meta-blocked">
          <Icon name="clock" size={15} />
          <span className="small">
            مضت {WHATSAPP_REPLY_WINDOW_HOURS} ساعة على آخر رسالة من العميل. واتساب لا يقبل بعدها
            إلا قالبًا معتمَدًا مسبقًا — تواصل معه بوسيلة أخرى.
          </span>
        </div>
      )}

      {linking && (
        <LinkConversationModal
          conversation={conversation}
          onClose={() => setLinking(false)}
          onLink={(body) => link.mutate(body)}
          linking={link.isPending}
        />
      )}
    </>
  );
}

/* ==========================================================================
   ربط المحادثة بسجل — أو صناعة السجل من المحادثة نفسها
   ========================================================================== */

/**
 * من يكتب إليك على واتساب ليس في النظام بعد — والغالب أنه عميلٌ محتمل.
 *
 * كان الربط يفترض أن السجلّ موجود: تبحث فلا تجد، فتغادر الشاشة لتنشئ
 * عميلًا محتملًا، ثم تعود لتربط — ثلاث خطواتٍ ورقمُ الهاتف يُنسخ يدويًّا
 * بينها. فصار للمحادثة ثلاثة أبواب في نافذةٍ واحدة:
 *
 *   - **ربطٌ بسجلٍّ قائم** — كما كان.
 *   - **عميلٌ محتملٌ جديد** — اسمُه ورقمُه مملوءان من المحادثة نفسها،
 *     ومصدرُه القناة التي جاء منها، ويُربط فور إنشائه.
 *   - **جهة اتصالٍ لعميلٍ قائم** — لمن كتب من رقمٍ جديد لشركةٍ نعرفها:
 *     تُضاف جهةً في ملفّها وتُربط المحادثة بالعميل.
 */
function LinkConversationModal({
  conversation,
  onClose,
  onLink,
  linking,
}: {
  conversation: MetaConversationView;
  onClose: () => void;
  onLink: (body: { leadId?: string | null; accountId?: string | null }) => void;
  linking: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'pick' | 'lead' | 'contact'>('pick');

  /* ما تعرفه المحادثة عنه: اسمُه المعروض ورقمُه — بذرةُ السجلّ الجديد. */
  const seedName = conversation.contactName ?? conversation.contactPhone ?? '';
  const seedPhone = conversation.contactPhone ?? '';

  const [name, setName] = useState(seedName);
  const [phone, setPhone] = useState(seedPhone);
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [accountId, setAccountId] = useState('');
  const [jobTitle, setJobTitle] = useState('');

  const accounts = useQuery({
    queryKey: ['accounts', 'options'],
    queryFn: () =>
      api<{ items: Array<{ id: string; name: string }> }>(
        `/crm/accounts${qs({ pageSize: 100, sort: 'name', order: 'asc' })}`,
      ),
    enabled: mode === 'contact',
  });

  /* المصدر يقول من أين جاء: واتساب أو ماسنجر أو إنستغرام لا «غير معروف». */
  const sourceOf = (kind: MetaChannelKind): string =>
    kind === 'WHATSAPP' ? 'واتساب' : kind === 'INSTAGRAM' ? 'إنستغرام' : 'ماسنجر';

  const createLead = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/crm/leads', {
        method: 'POST',
        body: {
          name: name.trim(),
          phone: phone.trim() || undefined,
          company: company.trim() || undefined,
          email: email.trim() || undefined,
          source: sourceOf(conversation.channelKind),
        },
      }),
    onSuccess: (created) => {
      toast('أُنشئ العميل المحتمل ورُبطت به المحادثة');
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
      onLink({ leadId: created.id, accountId: null });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر إنشاء العميل المحتمل.'),
  });

  const createContact = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/crm/contacts', {
        method: 'POST',
        body: {
          accountId,
          name: name.trim(),
          phone: phone.trim() || undefined,
          email: email.trim() || undefined,
          jobTitle: jobTitle.trim() || undefined,
        },
      }),
    onSuccess: () => {
      toast('أُضيفت جهة الاتصال ورُبطت المحادثة بالعميل');
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      void queryClient.invalidateQueries({ queryKey: ['account', accountId] });
      onLink({ accountId, leadId: null });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر إضافة جهة الاتصال.'),
  });

  const busy = linking || createLead.isPending || createContact.isPending;

  return (
    <Modal
      title="اربط المحادثة بسجل"
      onClose={onClose}
      footer={
        mode === 'pick' ? undefined : (
          <>
            <button className="btn" onClick={() => setMode('pick')} type="button">
              رجوع
            </button>
            <button
              className="btn btn-primary"
              disabled={
                busy ||
                name.trim().length < 2 ||
                (mode === 'contact' && !accountId)
              }
              onClick={() => (mode === 'lead' ? createLead.mutate() : createContact.mutate())}
            >
              {busy
                ? 'جارٍ الحفظ'
                : mode === 'lead'
                  ? 'أنشئ واربط'
                  : 'أضِف جهة الاتصال واربط'}
            </button>
          </>
        )
      }
    >
      {/* ثلاثة أبواب — والقائم أولها لأنه الأغلب. */}
      <div className="row mb-4" style={{ gap: 6, flexWrap: 'wrap' }}>
        {(
          [
            ['pick', 'سجلّ قائم', 'search'],
            ['lead', 'عميل محتمل جديد', 'funnel'],
            ['contact', 'جهة اتصال لعميل', 'users'],
          ] as Array<['pick' | 'lead' | 'contact', string, Parameters<typeof Icon>[0]['name']]>
        ).map(([k, label, icon]) => (
          <button
            key={k}
            type="button"
            className={`btn btn-sm${mode === k ? ' btn-primary' : ''}`}
            aria-pressed={mode === k}
            onClick={() => setMode(k)}
          >
            <Icon name={icon} size={13} />
            {label}
          </button>
        ))}
      </div>

      {mode === 'pick' ? (
        <RecordPicker
          onPick={(picked) =>
            onLink({ leadId: picked.leadId ?? null, accountId: picked.accountId ?? null })
          }
        />
      ) : (
        <div className="form-grid">
          <div className="field">
            <label className="label req" htmlFor="lk-name">
              الاسم
            </label>
            <input
              id="lk-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="اسم صاحب المحادثة"
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="lk-phone">
              الهاتف
            </label>
            <input
              id="lk-phone"
              className="input num"
              dir="ltr"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <p className="tiny mute">مأخوذٌ من المحادثة — عدّله إن لزم.</p>
          </div>

          {mode === 'contact' && (
            <div className="field">
              <label className="label req" htmlFor="lk-account">
                العميل
              </label>
              <select
                id="lk-account"
                className="select"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
              >
                <option value="">اختر عميلًا</option>
                {accounts.data?.items.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {mode === 'contact' ? (
            <div className="field">
              <label className="label" htmlFor="lk-job">
                المسمى الوظيفي
              </label>
              <input
                id="lk-job"
                className="input"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="مدير التسويق"
              />
            </div>
          ) : (
            <div className="field">
              <label className="label" htmlFor="lk-company">
                الشركة
              </label>
              <input
                id="lk-company"
                className="input"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
            </div>
          )}

          <div className="field span-2">
            <label className="label" htmlFor="lk-email">
              البريد الإلكتروني
            </label>
            <input
              id="lk-email"
              className="input num"
              dir="ltr"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {mode === 'lead' && (
            <p className="tiny mute span-2" style={{ margin: 0 }}>
              يُسجَّل مصدره «{sourceOf(conversation.channelKind)}» تلقائيًّا، وتُربط به هذه
              المحادثة فور إنشائه — فسجلّ تعامله يبدأ من أول رسالة.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ==========================================================================
   لوحة تفاصيل جهة الاتصال
   ========================================================================== */

/**
 * لوحةٌ تُظهر مَن خلف المحادثة: تفاصيله، وملخّص سجلّه، وآخر تغذيةٍ راجعة،
 * ومَن يتولّاها — عامّةً للجميع أو مخصَّصةً لموظف.
 *
 * التخصيص ليس زينة: الرسالة العامّة يراها الفريق كلّه، وتخصيصها لموظف يجعلها
 * مسؤوليّته هو فيُتابعها ولا تضيع بين الجميع. والقيمة معرّف المستخدم نفسه
 * الذي يُخزَّن في `ownerId`، فيُحلّ بدليل الفريق كسائر الشاشات.
 */
function ContactPanel({
  conversation,
  onClose,
}: {
  conversation: MetaConversationView;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { assignable, byId } = useTeamDirectory();

  const detail = useQuery({
    queryKey: ['meta', 'detail', conversation.id],
    queryFn: () =>
      api<MetaConversationDetail>(`/meta/conversations/${conversation.id}/detail`),
  });

  const assign = useMutation({
    mutationFn: (assigneeId: string | null) =>
      api(`/meta/conversations/${conversation.id}/assign`, {
        method: 'PATCH',
        body: { assigneeId },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['meta', 'detail', conversation.id] });
      void queryClient.invalidateQueries({ queryKey: ['meta', 'conversations'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر التخصيص.'),
  });

  const d = detail.data;
  const record = d?.record ?? null;
  const feedback = d?.lastFeedback ?? null;
  const assignee = d?.assigneeId ? byId.get(d.assigneeId) ?? null : null;

  return (
    <aside className="wa-panel">
      <header className="wa-panel-head">
        <h3 className="wa-panel-title">تفاصيل جهة الاتصال</h3>
        <button className="btn btn-sm btn-icon" onClick={onClose} aria-label="إغلاق">
          <Icon name="x" size={15} />
        </button>
      </header>

      <div className="wa-panel-body">
        {/* الهوية */}
        <div className="wa-panel-id">
          <Avatar name={conversation.contactName ?? conversation.externalId} size={64} />
          <strong className="wa-panel-name">
            {conversation.contactName ?? conversation.contactPhone ?? conversation.externalId}
          </strong>
          <span className="wa-panel-chan">
            <ChannelLogo kind={conversation.channelKind} size={13} />
            {META_CHANNEL_KIND_MAP[conversation.channelKind].label}
          </span>
          {conversation.contactPhone && (
            <a href={`tel:${conversation.contactPhone}`} className="wa-panel-phone num" dir="ltr">
              {conversation.contactPhone}
            </a>
          )}
        </div>

        {/* التخصيص: عامّة أو لموظف */}
        <section className="wa-panel-sec">
          <h4 className="wa-panel-h">
            <Icon name="users" size={13} /> مسؤول المحادثة
          </h4>
          <select
            className="select"
            value={d?.assigneeId ?? ''}
            onChange={(e) => assign.mutate(e.target.value || null)}
            disabled={!d || assign.isPending}
          >
            <option value="">عامّة — يراها الفريق كلّه</option>
            {assignable.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.name}
                {m.jobTitle ? ` — ${m.jobTitle}` : ''}
              </option>
            ))}
          </select>
          <p className="tiny mute">
            {assignee
              ? `مخصَّصةٌ لـ ${assignee.name} — هو من يتابعها.`
              : 'عامّةٌ الآن — خصِّصها لموظف ليتولّاها.'}
          </p>
        </section>

        {/* ملخّص السجلّ المربوط */}
        <section className="wa-panel-sec">
          <h4 className="wa-panel-h">
            <Icon name="clipboard" size={13} /> ملخّص العميل
          </h4>
          {detail.isPending ? (
            <div className="skeleton" style={{ height: 64 }} />
          ) : record ? (
            <div className="wa-panel-card">
              <div className="wa-panel-card-top">
                <Link
                  to={
                    record.kind === 'lead'
                      ? `/crm/leads/${d!.leadId}`
                      : `/crm/accounts/${d!.accountId}`
                  }
                  className="wa-panel-record"
                >
                  <Icon name={record.kind === 'lead' ? 'funnel' : 'building'} size={14} />
                  {record.name}
                </Link>
                {record.kind === 'lead' && record.status && (
                  <Badge kind={LEAD_STATUS_MAP[record.status as LeadStatus]?.kind ?? 'neutral'}>
                    {LEAD_STATUS_MAP[record.status as LeadStatus]?.label ?? record.status}
                  </Badge>
                )}
              </div>
              <dl className="wa-panel-facts">
                {record.kind === 'lead' && record.rating && (
                  <Fact
                    label="التقييم"
                    value={LEAD_RATING_MAP[record.rating as LeadRating]?.label ?? record.rating}
                  />
                )}
                {record.value != null && <Fact label="القيمة" value={money(record.value)} />}
                {record.source && <Fact label="المصدر" value={record.source} />}
                {record.extra && (
                  <Fact label={record.kind === 'lead' ? 'الشركة' : 'النشاط'} value={record.extra} />
                )}
                {record.email && <Fact label="البريد" value={record.email} />}
                {record.ownerId && (
                  <Fact label="المالك" value={byId.get(record.ownerId)?.name ?? '—'} />
                )}
                {record.lastContactedAt && (
                  <Fact label="آخر تواصل" value={date(record.lastContactedAt)} />
                )}
              </dl>
            </div>
          ) : (
            <p className="tiny mute">
              غير مربوطةٍ بعميل بعد. اربطها من رأس المحادثة ليظهر ملخّصه هنا.
            </p>
          )}
        </section>

        {/* آخر تغذيةٍ راجعة */}
        <section className="wa-panel-sec">
          <h4 className="wa-panel-h">
            <Icon name="note" size={13} /> آخر تغذية راجعة
          </h4>
          {detail.isPending ? (
            <div className="skeleton" style={{ height: 48 }} />
          ) : feedback ? (
            <blockquote className="wa-panel-fb">
              <p className="wa-panel-fb-body">{feedback.body ?? '—'}</p>
              <footer className="tiny mute">
                {feedback.authorName ?? 'غير معروف'} · {relative(feedback.createdAt)}
              </footer>
            </blockquote>
          ) : (
            <p className="tiny mute">
              {record ? 'لا تغذية راجعة مسجَّلة على هذا العميل بعد.' : 'اربطها بعميل لتظهر تغذيته الراجعة.'}
            </p>
          )}
        </section>
      </div>
    </aside>
  );
}

/** حقيقةٌ مفردة في ملخّص السجلّ: عنوانٌ ثم قيمة. */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="wa-fact">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/* ==========================================================================
   إدراج الرسائل المحفوظة — من الإعدادات إلى مسودّة الشات
   ========================================================================== */

/** زرّ 📄 بجوار حقل الكتابة — يفتح قائمة رسائل هذه القناة ويُدرج بنقرة. */
function SavedInsert({
  channel,
  onInsert,
}: {
  channel: MetaChannelKind;
  onInsert: (body: string) => void;
}) {
  const [open, setOpen] = useState(false);

  const saved = useQuery({
    queryKey: ['saved-messages'],
    queryFn: () =>
      api<{ items: Array<{ id: string; name: string; channel: string; body: string; isActive: boolean }> }>(
        '/callcenter/templates',
      ),
    enabled: open,
    staleTime: 60_000,
  });

  const items = (saved.data?.items ?? []).filter((t) => t.isActive && t.channel === channel);

  return (
    <span style={{ position: 'relative' }}>
      <button
        className="btn btn-sm btn-icon btn-ghost"
        onClick={() => setOpen((v) => !v)}
        title="الرسائل المحفوظة"
        aria-label="الرسائل المحفوظة"
      >
        <Icon name="note" size={16} />
      </button>

      {open && (
        <div className="qm-saved wa-saved-pop">
          {saved.isPending && <div className="skeleton" style={{ height: 50 }} />}
          {saved.isSuccess && items.length === 0 && (
            <p className="tiny mute" style={{ padding: 6 }}>
              لا رسائل محفوظة لهذه القناة — أضفها من «الإعدادات ← الرسائل المحفوظة».
            </p>
          )}
          {items.map((t) => (
            <button
              key={t.id}
              type="button"
              className="qm-saved-row"
              onClick={() => {
                onInsert(t.body);
                setOpen(false);
              }}
            >
              <strong className="tiny">{t.name}</strong>
              <span className="tiny mute">{t.body.slice(0, 60)}</span>
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

/* ==========================================================================
   الرسائل القادمة — ما جدولته إدارة العملاء لهذه القنوات
   ========================================================================== */

/**
 * أنشطة «رسالة» المجدولة بقنواتٍ غير البريد تظهر هنا — حيث تُرسَل —
 * فيفتح الموظف الصندوق ويعرف ماذا عليه أن يبعث ولمن.
 *
 * واتساب برقمٍ معلوم يبدأ محادثته بنقرة (معاملات الرابط نفسها التي
 * تفهمها هذه الشاشة)، وSMS من تطبيق الجهاز، وماسنجر وإنستغرام تُلتقط
 * محادثتاهما من القائمة. و«تمّ» شهادةُ المرسِل لأن الإرسال خارج علم
 * النظام — إلا واتساب المبدوء من هنا فرسالته تُسجَّل في المحادثة.
 */
function OutgoingQueue() {
  const queryClient = useQueryClient();
  const [collapsed, setCollapsed] = useState(false);

  const queue = useQuery({
    queryKey: ['crm', 'outgoing-messages'],
    queryFn: async () => {
      const page = await api<{ items: Array<Record<string, any>> }>(
        `/crm/activities${qs({ type: 'MAIL', status: 'PLANNED', sort: 'dueAt', order: 'asc', pageSize: 15 })}`,
      );
      return {
        items: page.items.filter(
          (t) => t.messageChannel && t.messageChannel !== 'EMAIL',
        ),
      };
    },
    refetchInterval: 60_000,
  });

  const complete = useMutation({
    mutationFn: (id: string) =>
      api(`/crm/activities/${id}/complete`, {
        method: 'POST',
        body: { outcome: 'أُرسلت من قنوات ميتا.' },
      }),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['crm', 'outgoing-messages'] }),
  });

  const items = queue.data?.items ?? [];
  if (items.length === 0) return null;

  const now = Date.now();

  return (
    <div className="wa-outgoing">
      <button
        type="button"
        className="wa-outgoing-head"
        onClick={() => setCollapsed((v) => !v)}
        aria-expanded={!collapsed}
      >
        <Icon name="send" size={13} />
        الرسائل القادمة
        <span className="num tiny">{items.length}</span>
        <Icon name={collapsed ? 'chevronDown' : 'chevronUp'} size={13} />
      </button>

      {!collapsed &&
        items.map((t) => {
          const who = t.lead ?? t.contact ?? t.account;
          const phone = t.lead?.phone ?? t.contact?.phone ?? t.account?.phone ?? null;
          const wa = whatsappNumber(phone);
          const overdue = t.dueAt && new Date(t.dueAt).getTime() < now;
          const channel = MESSAGE_CHANNEL_MAP[t.messageChannel as MessageChannel];
          const params = new URLSearchParams({
            ...(wa ? { to: wa } : {}),
            ...(t.leadId ? { lead: t.leadId } : {}),
            ...(t.accountId ? { account: t.accountId } : {}),
          });

          return (
            <div key={t.id} className="wa-outgoing-row">
              <div className="col" style={{ gap: 2, minWidth: 0, flex: 1 }}>
                <span className="small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.subject}
                </span>
                <span className="tiny mute">
                  {channel?.label ?? t.messageChannel}
                  {who ? ` · ${who.name}` : ''}
                  {t.dueAt && (
                    <span style={{ color: overdue ? 'var(--danger)' : undefined }}>
                      {' · '}
                      {overdue ? 'فات موعدها — ' : ''}
                      {relative(t.dueAt)}
                    </span>
                  )}
                </span>
              </div>

              {t.messageChannel === 'WHATSAPP' && wa && (
                <Link
                  className="btn btn-sm btn-icon btn-ghost"
                  to={`/crm/channels?${params.toString()}`}
                  title="افتح المحادثة"
                  aria-label="افتح المحادثة"
                >
                  <Icon name="chat" size={14} />
                </Link>
              )}
              {t.messageChannel === 'SMS' && phone && (
                <a
                  className="btn btn-sm btn-icon btn-ghost"
                  href={`sms:${phone}`}
                  title="SMS من تطبيق جهازك — لا تُسجَّل في النظام"
                  aria-label="أرسل SMS"
                >
                  <Icon name="sms" size={14} />
                </a>
              )}
              <button
                className="btn btn-sm btn-icon btn-ghost"
                disabled={complete.isPending}
                onClick={() => complete.mutate(t.id)}
                title="أُرسلت — علّمها منجزة"
                aria-label="تمّ الإرسال"
              >
                <Icon name="check" size={14} />
              </button>
            </div>
          );
        })}
    </div>
  );
}
